import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import api from '../../services/api';
import { coerceSignatureValue } from './types';

export type SignatureDisplayProps = {
  value: unknown;
  downloadPath: (storageKey: string) => string;
  height?: number;
  className?: string;
  emptyLabel?: string;
  /** Composite stamp behind signature when present (default true). */
  showStamp?: boolean;
};

function usePreview(storageKey: string | undefined, downloadPath: (k: string) => string) {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    let active = true;
    let revoke: (() => void) | null = null;
    setUrl(null);
    if (!storageKey) return;
    if (storageKey.startsWith('blob:') || storageKey.startsWith('data:')) {
      setUrl(storageKey);
      return;
    }
    void (async () => {
      try {
        const response = await api.get(downloadPath(storageKey), { responseType: 'blob' });
        const raw = response.data as Blob;
        const ct =
          (response.headers['content-type'] as string | undefined) || raw.type || 'image/png';
        const blob = raw.type ? raw : new Blob([raw], { type: ct });
        const objectUrl = window.URL.createObjectURL(blob);
        if (!active) {
          window.URL.revokeObjectURL(objectUrl);
          return;
        }
        revoke = () => window.URL.revokeObjectURL(objectUrl);
        setUrl(objectUrl);
      } catch {
        if (active) setUrl(null);
      }
    })();
    return () => {
      active = false;
      revoke?.();
    };
  }, [storageKey, downloadPath]);
  return url;
}

export const SignatureDisplay: React.FC<SignatureDisplayProps> = ({
  value,
  downloadPath,
  height = 96,
  className,
  emptyLabel = 'No signature',
  showStamp = true,
}) => {
  const { colors } = useTheme();
  const signature = coerceSignatureValue(value);
  const legacyText = typeof value === 'string' && value.trim() ? value.trim() : null;
  const signatureUrl = usePreview(signature?.storageKey, downloadPath);
  const stampUrl = usePreview(
    showStamp ? signature?.stamp?.storageKey : undefined,
    downloadPath,
  );

  if (signature) {
    return (
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border bg-white dark:bg-slate-950',
          colors.border,
          className,
        )}
        style={{ minHeight: height }}
      >
        {showStamp && stampUrl ? (
          <img
            src={stampUrl}
            alt=""
            className="pointer-events-none absolute inset-0 h-full w-full object-contain p-2 opacity-90"
          />
        ) : null}
        {signatureUrl ? (
          <img
            src={signatureUrl}
            alt="Signature"
            className="relative z-10 mx-auto max-h-full max-w-full object-contain p-2"
            style={{ maxHeight: height, minHeight: height * 0.6 }}
          />
        ) : (
          <div
            className={cn(
              'relative z-10 flex items-center justify-center text-xs py-6',
              colors.textMuted,
            )}
            style={{ minHeight: height }}
          >
            Loading…
          </div>
        )}
      </div>
    );
  }

  if (legacyText) {
    return (
      <p className={cn('text-sm italic', colors.textMuted, className)}>
        Typed signature: {legacyText}
      </p>
    );
  }

  return <p className={cn('text-xs', colors.textMuted, className)}>{emptyLabel}</p>;
};

export default SignatureDisplay;
